import type { OffsetPaginatedResponse } from '@utils/api/api-response';
import type { GetProp, TableProps } from 'antd';
import { Table } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import type { ColumnType as AntDColumnType } from 'antd/es/table/interface';
import type { ParsedQs } from 'qs';
import { parse, stringify } from 'qs';
import type { ForwardedRef, PropsWithoutRef, ReactNode } from 'react';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { TableFilters } from './hooks/use-filters';
import useFilters from './hooks/use-filters';

const stringifyFilters = (object: unknown) =>
  stringify(object, {
    indices: false,
    skipNulls: true,
    sort: (a: string, b: string) => a.localeCompare(b),
    encoder: (str, defaultEncoder, _, type) => {
      if (type === 'value' && str === '') {
        return 'null';
      }
      return defaultEncoder(str);
    },
  });

const parseParamsFromQueryString = (query: string) =>
  parse(query, {
    decoder: (str, defaultDecoder, _, type) => {
      if (type === 'value' && str === 'null') {
        return '';
      }
      return defaultDecoder(str);
    },
  });

const parseFiltersFromQueryString = (query: string) => {
  const parsedParams = parseParamsFromQueryString(query);

  if (!('filter' in parsedParams)) {
    return undefined;
  }

  return parsedParams['filter'] as ParsedQs;
};

const filtersObjectToTableFilters = (filters?: ParsedQs) =>
  filters
    ? (Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [
          key,
          Array.isArray(value) ? (value as string[]) : [value as string],
        ]),
      ) as TableFilters)
    : undefined;

// Re-declare forwardRef
declare module 'react' {
  function forwardRef<T, P = object>(
    render: (props: P, ref: React.Ref<T>) => ReactNode,
  ): (props: PropsWithoutRef<P> & React.RefAttributes<T>) => ReactNode;
}

export interface PaginatedFilteredTableRef {
  resetFilters: () => void;
}

export type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

export interface ColumnType<RecordType = AnyObject> extends AntDColumnType<RecordType> {
  key: string;
  filterable?: boolean;
}
interface ColumnGroupType<RecordType = AnyObject> extends Omit<ColumnType<RecordType>, 'dataIndex'> {
  children: ColumnsType<RecordType>;
}

export type ColumnsType<RecordType = AnyObject> = (ColumnGroupType<RecordType> | ColumnType<RecordType>)[];

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: TableFilters;
}

interface OwnProps<DataType extends Record<string, unknown>>
  extends Omit<TableProps<DataType>, 'pagination' | 'onChange' | 'columns'> {
  data: OffsetPaginatedResponse<DataType>;
  columns: ColumnsType<DataType>;
  onPageChange?: (page: number, pageSize: number, query?: string) => void;
  onFiltersChange?: (filters?: ParsedQs) => void;
}

const PaginatedFilteredTableInner = <DataType extends Record<string, unknown>>(
  props: OwnProps<DataType>,
  ref: ForwardedRef<PaginatedFilteredTableRef>,
): ReactNode => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, onPageChange, onFiltersChange, columns, prefixCls, dropdownPrefixCls, locale, ...tableProps } = props;
  const [tableData, setTableData] = useState<DataType[]>(data.data);
  const loading = tableProps.loading || false;

  const appliedFilters = useMemo(() => parseFiltersFromQueryString(searchParams.toString()), [searchParams]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: Number(searchParams.get('page')) || 1,
      pageSize: Number(searchParams.get('pageSize')) || 10,
    },
    filters: filtersObjectToTableFilters(appliedFilters),
  });

  useEffect(() => {
    setTableParams((params) => ({
      ...params,
      filters: filtersObjectToTableFilters(appliedFilters),
    }));
    onFiltersChange?.(appliedFilters);
  }, [appliedFilters, onFiltersChange]);

  const resetFilters = () => {
    setTableParams((params) => ({
      ...params,
      filters: undefined,
    }));
  };

  useImperativeHandle(ref, () => ({
    resetFilters,
  }));

  const [transformColumns] = useFilters({
    filters: tableParams.filters,
    prefixCls,
    locale,
    dropdownPrefixCls,
    mergedColumns: columns,
  });
  const tableColumns = transformColumns(columns);

  useEffect(() => {
    setTableData(data.data);
  }, [data.data]);

  useEffect(() => {
    setTableParams((params) => ({
      ...params,
      pagination: {
        ...params.pagination,
        current: data.meta?.current,
        pageSize: data.meta?.pageSize,
        total: data.meta?.total,
      },
    }));
  }, [data.meta]);

  const handleTableChange: TableProps['onChange'] = (pagination, filters) => {
    const previousTableParams = tableParams;
    setTableParams((params) => ({
      ...params,
      pagination,
      filters,
    }));

    if (pagination.pageSize !== previousTableParams.pagination?.pageSize) {
      setTableData([]);
    }

    if (
      pagination.current &&
      pagination.pageSize &&
      (previousTableParams.pagination?.current !== pagination.current ||
        previousTableParams.pagination?.pageSize !== pagination.pageSize)
    ) {
      setSearchParams(
        (params) =>
          new URLSearchParams({
            ...Object.fromEntries(params),
            ...{
              page: `${pagination.current}`,
              pageSize: `${pagination.pageSize}`,
            },
          }),
      );
      onPageChange?.(
        pagination.current,
        pagination.pageSize,
        filters ? stringifyFilters({ filter: filters }) : undefined,
      );
    }

    // TODO: Instead of comparing query strings, we can use deep object comparison
    const currentFilters = stringifyFilters({ filter: filters });
    const previousFilters = stringifyFilters({ filter: tableParams.filters });
    if (currentFilters !== previousFilters) {
      setSearchParams(
        (params) =>
          new URLSearchParams({
            ...(params.has('page')
              ? {
                  page: '1', // reset to first page if filters are changed
                  pageSize: params.get('pageSize')!,
                }
              : {}),
            ...Object.fromEntries(new URLSearchParams(currentFilters)),
          }),
      );
    }
  };

  return (
    <Table<DataType>
      {...tableProps}
      dataSource={tableData}
      columns={tableColumns}
      pagination={tableParams.pagination}
      loading={loading}
      onChange={handleTableChange}
    />
  );
};

const PaginatedFilteredTable = forwardRef(PaginatedFilteredTableInner);

export default PaginatedFilteredTable;
